// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React, {useEffect} from 'react';
import {redux} from '@app/redux';
import {connect, useDispatch, useSelector} from 'react-redux';
import {withRouter} from 'react-router';
import {TabBar} from '@app/components/TabBar/TabBar';
import {CX_BLOCK_TYPES, renderTitle} from '@app/constants';
import {npsEnabled} from '@app/utils/dom';
import {FeedbackListViewer} from './feedback-list-viewer';

import {GET_CX_METRIC_ASPECT_FEEDBACKS} from '@app/redux/feedback/constants';
import {areAspectFeedbackMessagesFetchingSelector} from '@app/redux/feedback/selectors';
import {
    changeSelectedAspect,
    changeSelectedMaxDate,
    changeSelectedMessage,
    changeSelectedMetricId,
    changeSelectedMinDate,
} from '@app/redux/feedback/actions';
import {MdTune} from 'react-icons/md';
import {Button, Tooltip} from '@app/common';
import {FilterMenu} from './filter-menu';
import {DateRange} from '@app/containers/DateRange/DateRange';
import moment from 'moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

// Renders the feedback page separated in different tabs by CX Type.
export const FeedbackComponent = (props) => {
    const {
        aspectFeedbacks,
        selectedAspect,
        getAspectFeedbacks,
        minDate,
        maxDate,
    } = props;

    const dispatch = useDispatch();
    const filtersEnabled = false;

    // Creates all the CX tabs and filters out the ones disabled by the application.
    const populateTabsByCxType = () => {
        return Object.keys(CX_BLOCK_TYPES)
            .map((cxType) => ({
                value: CX_BLOCK_TYPES[cxType],
                label: renderTitle(CX_BLOCK_TYPES[cxType]),
            }))
            .filter(
                (cxTab) =>
                    (cxTab.value === CX_BLOCK_TYPES.NPS && npsEnabled()) ||
                    cxTab.value !== CX_BLOCK_TYPES.NPS,
            );
    };

    const isRefreshing = useSelector((state) =>
        areAspectFeedbackMessagesFetchingSelector(state),
    );

    // Set the default active tab.
    useEffect(() => {
        if (!selectedAspect && !minDate && !maxDate) {
            const cxType = npsEnabled()
                ? CX_BLOCK_TYPES.NPS
                : CX_BLOCK_TYPES.CSAT;
            const minDate = parseMoment(
                moment()
                    .subtract(7, 'd')
                    .set({
                        hour: 0,
                        minute: 0,
                        second: 0,
                        millisecond: 0,
                    })
                    .unix(),
            );
            const maxDate = parseMoment(
                moment()
                    .set({hour: 23, minute: 59, second: 59, millisecond: 1})
                    .unix(),
            );
            dispatch(changeSelectedMinDate(minDate));
            dispatch(changeSelectedMaxDate(maxDate));
            setActiveTab(cxType, minDate, maxDate);
        }
    });

    // Set the default metric Id
    useEffect(() => {
        if (aspectFeedbacks && aspectFeedbacks.length > 0 && !isRefreshing) {
            dispatch(
                changeSelectedMetricId(aspectFeedbacks[0].body?.metric_id),
            );
            handleClickMessage(aspectFeedbacks[0]);
        }
    }, [isRefreshing]);

    const handleClickMessage = (msg) => {
        dispatch(changeSelectedMessage(msg));
    };

    const fetchAspectFeedbackMessages = (cxType, minDate, maxDate) => {
        getAspectFeedbacks(cxType, minDate, maxDate);
    };

    const setActiveTab = (cxType, minDate, maxDate) => {
        if (cxType !== selectedAspect && !isRefreshing && minDate && maxDate) {
            dispatch(changeSelectedAspect(cxType));
            fetchAspectFeedbackMessages(cxType, minDate, maxDate);
        }
    };

    const setMinDate = (date) => {
        const minDate = date.set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        dispatch(changeSelectedMinDate(parseMoment(minDate)));
        fetchAspectFeedbackMessages(
            selectedAspect,
            parseMoment(minDate),
            maxDate,
        );
    };

    const setMaxDate = (date) => {
        const maxDate = date.set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 1,
        });
        dispatch(changeSelectedMaxDate(parseMoment(maxDate)));
        fetchAspectFeedbackMessages(
            selectedAspect,
            minDate,
            parseMoment(maxDate),
        );
    };

    const parseMoment = (d) => moment(d, 'X');

    return (
        <div className="feedback-container">
            <span className="title">
                <h1>Feedback</h1>
            </span>
            <div className="filters">
                <div className="filters-datepicker">
                    {minDate && maxDate && (
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <DateRange
                                minDate={parseMoment(minDate)}
                                setMinDate={(e) => setMinDate(e)}
                                maxDate={parseMoment(maxDate)}
                                setMaxDate={(e) => setMaxDate(e)}
                            />
                        </MuiPickersUtilsProvider>
                    )}
                </div>
                {filtersEnabled && (
                    <div className="filter-menu">
                        <Tooltip
                            overlay={<FilterMenu />}
                            tooltipProps={{
                                placement: 'bottomLeft',
                                overlayStyle: {
                                    width: '400px',
                                },
                            }}
                            isSimple={true}
                        >
                            <Button className="filter-button" outlined>
                                <span className="icon">
                                    <MdTune size={20} />
                                </span>
                                <span className="label">Filter</span>
                                <hr
                                    className="divider"
                                    //@ts-ignore
                                    width={2}
                                    size={25}
                                />
                                <span className="filter-count">0</span>
                            </Button>
                        </Tooltip>
                    </div>
                )}
            </div>

            <TabBar
                setActiveTab={(e) => setActiveTab(e, minDate, maxDate)}
                activeTab={selectedAspect}
                tabs={populateTabsByCxType()}
            />
            <FeedbackListViewer
                messages={aspectFeedbacks}
                cxType={selectedAspect}
                handleClick={handleClickMessage}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        aspectFeedbacks:
            redux.feedbacks.selectors.aspectFeedbacksSelector(state),
        selectedAspect: redux.feedbacks.selectors.getSelectedAspect(state),
        selectedMetricId: redux.feedbacks.selectors.getSelectedMetricId(state),
        minDate: redux.feedbacks.selectors.getSelectedMinDate(state),
        maxDate: redux.feedbacks.selectors.getSelectedMaxDate(state),
    };
};

export const getAspectFeedbacksDispatcher =
    (dispatch) => (aspect, minDate, maxDate) => {
        dispatch(
            redux.rest.actions[GET_CX_METRIC_ASPECT_FEEDBACKS].force({
                aspect,
                minDate: minDate.unix(),
                maxDate: maxDate.unix(),
            }),
        );
    };

const mapDispatchToProps = (dispatch) => ({
    getAspectFeedbacks: getAspectFeedbacksDispatcher(dispatch),
});

export const Feedback = withRouter(
    connect(mapStateToProps, mapDispatchToProps)(FeedbackComponent),
);
